/**
 * 業務フロー 1 本とステージの図の組み立て（API §4 `getFlow` の出力の形）。**BigQuery を知らない。**
 *
 * 業務フロー 1 本（節点 ＝ 業務、枝 ＝ 結末の行 ＋ 宛先の型、レーン ＝ 業務のアクター）と、
 * ステージ 1 つ（出口 ＋ 主フロー ＋ 支援フローの札）を、案件を重ねた近傍から組む。違反は
 * core の検査（`flow-checks.ts` / `stage-checks.ts`）を近傍に当てて取る。
 *
 * **差分の印は持たない。** 新規 / 更新 / 削除 の印は案件の変更から画面が畳む。姿と印を同じ
 * 応答に混ぜると、印の出どころが 2 本になる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件を重ねた近傍から、業務フロー 1 本（型・入口・出口・レーン・節点・枝・役割の整合・違反）とステージの図を組み立てる
 * @graph-connects none
 */
import { findBranchesWithoutTarget, findExitEndingMismatches, findFlowBranchesMissingEndings, findFlowEntryExitMismatches, findFlowKindRoleMismatches, findOversizedFlows, findParallelSplitsWithoutJoin, findSupportFlowTriggerStageViolations, findUnreachableFlowNodes, } from '../../core/elements/index.js';
import { idOf, ids, labelOf, nodeOf, recordOf, recordsOf, refOf, stageFlowIds, } from './flow-fields.js';
/**
 * 入口の種類。主の 2 種（前のステージの出口・鎖の最初）と支援の引き金 4 種。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務フローの入口の種類 6 値（前のステージ・鎖の最初・時間・操作・主フローの結末・外部の出来事）
 * @graph-connects none
 */
export const FLOW_ENTRY_VIEW_KINDS = [
    'previousStage',
    'streamStart',
    'timer',
    'operation',
    'ending',
    'external',
];
/**
 * 出口の種類。型で決まる（主 ＝ ステージの出口、入れる ＝ 渡す先、出す ＝ 外への作用、
 * 割り込み ＝ 中止か前のステージ）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務フローの出口の種類 5 値（ステージの出口・渡す先・外への作用・中止・前のステージ）
 * @graph-connects none
 */
export const FLOW_EXIT_VIEW_KINDS = [
    'stageExit',
    'handoff',
    'effect',
    'abort',
    'previousStage',
];
/**
 * 鎖の中の置き場を、ラベルに解いた形にする。
 *
 * @param nodes 前後のステージの名前の材料を含む姿
 * @param place `stageChainPlaceOf` の答え
 * @returns ラベルに解いた置き場
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 鎖の中の置き場（順・前のステージ・次のステージ）の番号をラベルに解く
 * @graph-connects refOf [calls] 参照のラベル解決
 */
export function stageChainViewOf(nodes, place) {
    return {
        position: place.position,
        prev: place.prevStageId === null ? null : refOf(nodes, place.prevStageId),
        next: place.nextStageIds.map((id) => refOf(nodes, id)),
    };
}
/**
 * 業務フローの欄が指す番号（ステージ・状況・節点の業務・入口・出口・枝の結末）。
 * @graph-connects none
 */
function flowReferenceIds(properties) {
    const entry = recordOf(properties['entry']) ?? {};
    const exit = recordOf(properties['exit']) ?? {};
    return ids([
        idOf(properties, 'stage'),
        idOf(properties, 'situation'),
        ...recordsOf(properties['nodes']).map((node) => idOf(node, 'business')),
        ...['business', 'ending', 'term', 'actor'].map((field) => idOf(entry, field)),
        ...['business', 'ending', 'stage', 'counterpart'].map((field) => idOf(exit, field)),
        ...recordsOf(exit['endings']).flatMap((row) => [idOf(row, 'business'), idOf(row, 'ending')]),
        ...recordsOf(properties['transitions']).map((transition) => idOf(transition, 'ending')),
    ]);
}
/**
 * 図に要るノード 1 件が、さらに指す番号。
 *
 * ラベルの材料（クラスの用語・動詞・属するクラス）に加えて、業務のアクターと業務ルール
 * （制約 63 の値の流れ）、ステージの入口と出口の結末の行を辿る。
 * **業務ルールの先は辿らない** — 値の流れが見るのは業務ルールの欄の番号だけで、ラベルは出さない。
 * @graph-connects flowReferenceIds [calls] 業務フローの欄が指す番号
 * @graph-connects recordOf [calls] 欄の値をオブジェクトとして読む
 * @graph-connects idOf [calls] 欄の値を要素の id として読む
 */
function neighborIds(value) {
    const { properties } = value;
    switch (value.nodeType) {
        case 'Flow':
            return flowReferenceIds(properties);
        case 'Stage': {
            const entry = recordOf(properties['entry']) ?? {};
            const exit = recordOf(properties['exit']) ?? {};
            return ids([
                idOf(properties, 'subject'),
                idOf(properties, 'verb'),
                idOf(entry, 'business'),
                idOf(entry, 'ending'),
                idOf(exit, 'business'),
                idOf(exit, 'ending'),
            ]);
        }
        case 'Business':
            return ids([
                idOf(properties, 'subject'),
                idOf(properties, 'verb'),
                idOf(properties, 'actor'),
                ...(Array.isArray(properties['rules'])
                    ? properties['rules'].map((rule) => typeof rule === 'number' && Number.isInteger(rule) && rule > 0 ? rule : null)
                    : []),
            ]);
        case 'Actor':
            return ids([idOf(properties, 'subject'), idOf(properties, 'qualifier')]);
        case 'Term':
            return properties['section'] === 'property' ? ids([idOf(properties, 'ownerClass')]) : [];
        default:
            return [];
    }
}
/**
 * 起点から辿れる近傍の番号（起点を含む）。**読めていないノードの先は辿れない**ので、呼び出し
 * 側は読めていない番号を読み足してから再び呼ぶ。
 *
 * @param nodes 案件を当てた姿
 * @param roots 起点
 * @returns 辿れた番号（起点を含む）
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務フローの読取の起点（ステージとその業務フロー）から、図と検査に要る近傍の番号を辿って集める
 * @graph-connects neighborIds [calls] ノード 1 件が指す番号
 */
export function collectFlowNeighborhood(nodes, roots) {
    const seen = new Set(roots);
    const queue = [...roots];
    for (let current = queue.shift(); current !== undefined; current = queue.shift()) {
        const value = nodes.get(current);
        if (value === undefined)
            continue;
        for (const next of neighborIds(value)) {
            if (seen.has(next))
                continue;
            seen.add(next);
            queue.push(next);
        }
    }
    return seen;
}
/**
 * そのフローに当たる違反を、core の検査をフロー単位で当てて取る（制約 52 / 53 / 54 / 59 / 63 /
 * 64 / 66 / 68 / 69）。ステージを数える制約（61 / 62）とグラフ全体を数える制約は、フロー 1 本
 * の図の違反ではないので当てない。
 *
 * @param graph 近傍を型ごとに読み分けたグラフ
 * @param flowId 違反を取るフロー
 * @returns そのフローの違反（制約番号の昇順）
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務フロー 1 本に当たる制約違反を、core のフローの検査を近傍に当てて集める
 * @graph-connects findFlowBranchesMissingEndings [calls] 制約 52
 * @graph-connects findExitEndingMismatches [calls] 制約 53
 * @graph-connects findBranchesWithoutTarget [calls] 制約 54
 * @graph-connects findUnreachableFlowNodes [calls] 制約 59
 * @graph-connects findFlowKindRoleMismatches [calls] 制約 63
 * @graph-connects findParallelSplitsWithoutJoin [calls] 制約 64
 * @graph-connects findOversizedFlows [calls] 制約 66
 * @graph-connects findFlowEntryExitMismatches [calls] 制約 68
 * @graph-connects findSupportFlowTriggerStageViolations [calls] 制約 69
 */
export function flowViolations(graph, flowId) {
    return [
        ...findFlowBranchesMissingEndings(graph),
        ...findExitEndingMismatches(graph),
        ...findBranchesWithoutTarget(graph),
        ...findUnreachableFlowNodes(graph),
        ...findFlowKindRoleMismatches(graph),
        ...findParallelSplitsWithoutJoin(graph),
        ...findOversizedFlows(graph),
        ...findFlowEntryExitMismatches(graph),
        ...findSupportFlowTriggerStageViolations(graph),
    ]
        .filter((violation) => violation.nodeId === flowId)
        .sort((left, right) => left.constraintNumber - right.constraintNumber);
}
/** 入口を組む。 @graph-connects refOf [calls] 参照のラベル解決 */
function entryViewOf(nodes, flow) {
    const empty = { business: null, ending: null, term: null, actor: null };
    const { entry } = flow;
    switch (entry.kind) {
        case 'streamStart':
            return { kind: entry.kind, ...empty };
        case 'previousStage':
        case 'ending':
            return {
                kind: entry.kind,
                ...empty,
                business: refOf(nodes, entry.business),
                ending: refOf(nodes, entry.ending),
            };
        case 'timer':
        case 'external':
            return { kind: entry.kind, ...empty, term: refOf(nodes, entry.term) };
        case 'operation':
            return { kind: entry.kind, ...empty, actor: refOf(nodes, entry.actor) };
    }
}
/** 結末の行を組む。 @graph-connects refOf [calls] 参照のラベル解決 */
function endingRowOf(nodes, row) {
    return { business: refOf(nodes, row.business), ending: refOf(nodes, row.ending) };
}
/**
 * 出口を組む。次のステージは主（ステージの出口）だけが持つ。
 * @graph-connects endingRowOf [calls] 結末の行
 */
function exitViewOf(nodes, flow, nextStages) {
    const empty = {
        business: null,
        ending: null,
        stage: null,
        counterpart: null,
        effect: null,
        endings: [],
        nextStages: [],
    };
    if (flow.type === 'main') {
        const row = endingRowOf(nodes, flow.exit);
        return { kind: 'stageExit', ...empty, business: row.business, ending: row.ending, nextStages };
    }
    const endings = flow.exit.endings.map((row) => endingRowOf(nodes, row));
    switch (flow.type) {
        case 'inbound':
            return { kind: 'handoff', ...empty, business: refOf(nodes, flow.exit.business), endings };
        case 'outbound':
            return {
                kind: 'effect',
                ...empty,
                counterpart: refOf(nodes, flow.exit.counterpart),
                effect: flow.exit.effect,
                endings,
            };
        case 'interrupt':
            return flow.exit.kind === 'abort'
                ? { kind: 'abort', ...empty, endings }
                : { kind: 'previousStage', ...empty, stage: refOf(nodes, flow.exit.stage), endings };
    }
}
/** 札 1 枚を組む。 @graph-connects entryViewOf [calls] 入口 @graph-connects exitViewOf [calls] 出口 */
function cardOf(nodes, flowId, flow, nextStages) {
    return {
        flowId,
        label: labelOf(nodes, flowId),
        type: flow.type,
        situation: flow.type === 'main' ? null : refOf(nodes, flow.situation),
        entry: entryViewOf(nodes, flow),
        exit: exitViewOf(nodes, flow, nextStages),
    };
}
/**
 * 業務フロー 1 本を組む。**BigQuery を知らない純関数である。**
 *
 * レーンは節点の並びで**アクターが初めて現れた順**に並べる。id の昇順で並べると、図の帯が
 * 流れの順と無関係に並び、読み手が上から下へ辿れない。
 *
 * @param graph 近傍を型ごとに読み分けたグラフ（案件を当てた姿）
 * @param flowId 組むフローの id
 * @param updatedAt そのフローの更新日時
 * @param nextStages フローが属するステージの出口の結末の行で続く次のステージ（主の出口に載せる）
 * @returns フロー 1 本。フローが無い・契約の形に読めなければ null
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 近傍のグラフから業務フロー 1 本（型・入口・出口・レーン・節点・枝・役割の整合・違反）を組む
 * @graph-connects cardOf [calls] 型・状況・入口・出口
 * @graph-connects flowViolations [calls] そのフローの違反
 */
export function buildFlowView(graph, flowId, updatedAt, nextStages) {
    const flow = graph.flows.get(flowId);
    if (flow === undefined)
        return null;
    const { nodes } = graph;
    const card = cardOf(nodes, flowId, flow, nextStages);
    const flowNodes = flow.nodes.map((node) => {
        if (node.kind !== 'business') {
            return { nodeId: node.id, kind: node.kind, business: null, actor: null };
        }
        const business = nodeOf(nodes, node.business, 'Business');
        const actorId = business === null ? null : idOf(business.properties, 'actor');
        return {
            nodeId: node.id,
            kind: node.kind,
            business: refOf(nodes, node.business),
            actor: actorId === null ? null : refOf(nodes, actorId),
        };
    });
    const transitions = flow.transitions.map((transition) => {
        const to = transition.to ?? null;
        const ending = transition.ending ?? null;
        const head = `${String(transition.from)}-${to === null ? transition.destinationKind : String(to)}`;
        return {
            id: ending === null ? head : `${head}@${String(ending)}`,
            from: transition.from,
            to,
            destinationKind: transition.destinationKind,
            ending,
            label: ending === null ? null : labelOf(nodes, ending),
        };
    });
    const lanes = [];
    for (const node of flowNodes) {
        if (node.actor === null)
            continue;
        if (lanes.some((lane) => lane.actorId === node.actor?.id))
            continue;
        lanes.push({ actorId: node.actor.id, label: node.actor.label });
    }
    const violations = flowViolations(graph, flowId);
    const reasons = violations
        .filter((violation) => violation.constraintNumber === 63)
        .map((violation) => violation.message);
    return {
        flowId,
        label: card.label,
        updatedAt,
        type: flow.type,
        stage: refOf(nodes, flow.stage),
        situation: card.situation,
        entry: card.entry,
        exit: card.exit,
        lanes,
        nodes: flowNodes,
        transitions,
        roleCheck: { declared: flow.type, consistent: reasons.length === 0, reasons },
        violations,
    };
}
/**
 * ステージの入口か出口の結末の行を読む。欠ければ null。
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business ステージの入口か出口の結末の行を読む
 * @graph-connects endingRowOf [calls] 結末の行
 * @graph-connects recordOf [calls] 欄の値をオブジェクトとして読む
 * @graph-connects idOf [calls] 欄の値を要素の id として読む
 */
function stageEndingRowOf(nodes, stage, field) {
    const row = recordOf(stage.properties[field]);
    if (row === null)
        return null;
    const business = idOf(row, 'business');
    const ending = idOf(row, 'ending');
    return business === null || ending === null ? null : endingRowOf(nodes, { business, ending });
}
/**
 * ステージの図 1 枚を組む。**BigQuery を知らない純関数である。**
 *
 * 主フローは宣言した型（`type: 'main'`）で引く。2 本以上あれば id の小さい方を描き、本数の
 * 誤りは制約 62 に任せる。契約の形に読めない業務フローは札に出さない。
 *
 * 鎖の中の置き場（`chain`）は受け取るだけで組まない — 前後のステージは近傍の外に居るので、
 * 読取がステージ全体から導いて渡す。
 *
 * @param graph 近傍を型ごとに読み分けたグラフ（案件を当てた姿）
 * @param stageId 組むステージの id
 * @param updatedAtOf 番号 → 更新日時
 * @param chain そのステージの鎖の中の置き場
 * @returns ステージの図。ステージが無ければ null
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 近傍のグラフからステージの図（入口と出口・説明・鎖の置き場・主フロー 1 本・支援フローの札）を組む
 * @graph-connects buildFlowView [calls] 主フローの組み立て
 * @graph-connects cardOf [calls] 支援フローの札
 * @graph-connects stageEndingRowOf [calls] ステージの入口か出口の結末の行を読む
 * @graph-connects localizedOf [calls] 説明の 3 言語の本文
 */
export function buildFlowStageView(graph, stageId, updatedAtOf, chain) {
    const { nodes } = graph;
    const stage = nodeOf(nodes, stageId, 'Stage');
    if (stage === null)
        return null;
    const flows = stageFlowIds(nodes, stageId).flatMap((id) => {
        const flow = graph.flows.get(id);
        return flow === undefined ? [] : [{ id, flow }];
    });
    const main = flows.find((entry) => entry.flow.type === 'main');
    return {
        stage: {
            stageId,
            label: labelOf(nodes, stageId),
            updatedAt: updatedAtOf(stageId),
            entry: stageEndingRowOf(nodes, stage, 'entry'),
            exit: stageEndingRowOf(nodes, stage, 'exit'),
            description: localizedOf(stage.properties['description']),
            chain,
        },
        mainFlow: main === undefined ? null : buildFlowView(graph, main.id, updatedAtOf(main.id), chain.next),
        supportFlows: flows
            .filter((entry) => entry.flow.type !== 'main')
            .map((entry) => cardOf(nodes, entry.id, entry.flow, [])),
    };
}
/**
 * 欄の値を 3 言語の本文として読む。組でなければ null。
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 欄の値を 3 言語の本文として読む
 * @graph-connects recordOf [calls] 欄の値をオブジェクトとして読む
 */
function localizedOf(raw) {
    const record = recordOf(raw);
    if (record === null)
        return null;
    const { ja, en, vi } = record;
    return typeof ja === 'string' && typeof en === 'string' && typeof vi === 'string'
        ? { ja, en, vi }
        : null;
}
//# sourceMappingURL=flow-view.js.map